import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../environments/environment';
import { ConfirmationComponent } from '../confirmation/confirmation.component';
import { MailingConfirmationComponent } from '../mailing confirmation/mailing-confirmation.component';
import { BeneficiaryContactDto } from '../models/beneficiary-contact.dto';
import { ContactDto } from '../models/contact.dto';
import { MailingDto } from '../models/mailing.dto';
import { MailService } from '../services/mail.service';
import { ToastService } from '../toast/toast.service';
import { Utils } from '../utils';

@Component({
  selector: 'app-mailing-composer',
  templateUrl: './mailing-composer.component.html',
  standalone: false,
})
export class MailingComposerComponent implements OnInit {
  @Input()
  public beneficiarySelected: number[];
  private beneficiariesNoMail: string[] = [];
  public form: UntypedFormGroup;
  public submitted: boolean = false;
  public contacts: ContactDto[] = [];
  private subject: string = null;
  private content: string = null;
  private count: number;

  // Attachments
  public attachmentsForm: UntypedFormGroup;
  public maxFileSize: number = environment.UPLOAD_MAX_FILESIZE ?? 1000 * 1000 * 1.2; // TODO : à revoir
  public maxFileSizeStr: string = Utils.humanFileSize(this.maxFileSize, true, 2);

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly mailService: MailService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.count = this.beneficiarySelected.length;
    this.contacts = await this.mailService.getContacts();
    this.loadForm();
  }

  private loadForm(): void {
    this.form = this.formBuilder.group({
      from: [{ name: 'Insertis', email: 'noreply@grandlyon.com' } as ContactDto, Validators.required, false],
      cc: [],
      subject: [this.subject],
      to: [this.count + ' bénéficiaire' + (this.count > 1 ? 's' : '')],
      content: [this.content, Validators.required],
    });
    this.form.controls.from.disable();

    this.initAttachementForm();
  }

  public onInit(beneficiarySelected: number[], subject: string = '', content: string = null): void {
    this.subject = subject;
    this.content = content;
    this.beneficiarySelected = beneficiarySelected;
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (this.form.valid && this.attachmentsForm.valid && this.beneficiarySelected) {
      const message: MailingDto = {
        cc: this.form.controls.cc.value,
        subject: this.form.controls.subject.value,
        content: this.form.controls.content.value,
        beneficiaryIds: this.beneficiarySelected,
        attachments: (this.attachments.value ?? []).filter((value) => value.document !== null),
      };

      if (Utils.isNotEmpty(message.subject) || (!Utils.isNotEmpty(message.subject) && (await this.openConfirmationModal()))) {
        const beneficiariesNoMail: BeneficiaryContactDto[] = await this.mailService.sendMailList(message);
        this.cancel();

        if (beneficiariesNoMail?.length) {
          beneficiariesNoMail.forEach((element: BeneficiaryContactDto) => {
            this.beneficiariesNoMail.push(element.name);
          });
          await this.openAlertModal();
        } else if (!beneficiariesNoMail || this.beneficiarySelected.length > beneficiariesNoMail.length) {
          this.toastService.show('Votre message électronique a été envoyé avec succès.', 'success');
        }
      }
    }
  }

  public cancel(): void {
    this.modal.close();
  }

  public compareContacts: (item: ContactDto, selected: ContactDto) => boolean = (item: ContactDto, selected: ContactDto): boolean =>
    item.email === selected.email;

  public searchContacts: (term: string, item: ContactDto) => boolean = (term: string, item: ContactDto): boolean => {
    term = term.toLowerCase();
    return item.name.toLowerCase().indexOf(term) > -1 || item.email.toLowerCase().indexOf(term) > -1;
  };

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Voulez-vous envoyer ce message sans objet ?');
  }

  private async openAlertModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(MailingConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: MailingConfirmationComponent = modal.componentInstance as MailingConfirmationComponent;
    return await modalComponent.confirm("L'email n'est pas envoyé aux bénéficiaires suivants: ", this.beneficiariesNoMail);
  }

  // *********************************************************************************************
  // * ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT A *
  // * ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT A *
  // *********************************************************************************************

  private initAttachementForm(): void {
    this.attachmentsForm = this.formBuilder.group({
      attachments: this.formBuilder.array([]),
    });
    this.addAttachment();
  }

  public get attachments(): UntypedFormArray {
    return this.attachmentsForm.controls.attachments as UntypedFormArray;
  }

  public addAttachment(): void {
    this.attachments.push(this.formBuilder.group({ document: [] }));
  }

  public removeAttachment(index: number): void {
    if (this.attachments.length === 1) {
      this.attachments.at(0).setValue({ document: null });
    } else {
      this.attachments.removeAt(index);
    }
  }
}
